import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Search, PlusCircle, Clock } from "lucide-react";
import { API } from "../api";
import { useAuth } from "../context/AuthContext";
import { CARD, PageNav } from "../components/PageChrome";
import OrgPicker from "../components/OrgPicker";
import CreateOrgForm from "../components/CreateOrgForm";
import InvitationCard from "../components/InvitationCard";
import { ActionButton, ErrorNote, Skeleton } from "../components/Feedback";

// The gate an unaffiliated user meets after signing in: an invitation, else the request
// in flight, else the choice between joining an organisation and proposing one.
export default function Onboarding() {
  const { user, logout, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [choice, setChoice] = useState(null); // "join" | "create"

  const load = useCallback(async () => {
    try {
      const res = await API.get("/api/orgs/onboarding");
      setState(res.data);
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "We could not read your organisation status. Reload to try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A decision may have granted membership; refresh() re-reads /api/me so the app
  // leaves this gate on its own rather than waiting for a reload.
  const afterDecision = useCallback(async () => {
    await refresh();
    await load();
  }, [refresh, load]);

  const invitations = state?.invitations || [];
  const joinRequest = state?.join_request;
  const orgRequest = state?.org_request;
  const waiting = joinRequest || orgRequest;

  return (
    <div className="min-h-screen bg-slate-200 text-slate-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-100/70 via-transparent to-transparent" />

      <PageNav label="Registry" user={user} onLogout={logout} />

      {/* Same column as the Hub users land on next, so the page does not jump. */}
      <main className="mx-auto max-w-5xl px-6 pb-20 pt-28">
        <section className={`${CARD} p-8 md:p-12`}>
          <header className="mb-10">
            <h1 className="text-2xl font-black leading-[1.1] tracking-tighter text-balance sm:text-3xl">
              Which institution are you publishing for?
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Every datasource in the NEED Catalog is registered on behalf of an
              organisation. Join yours to reach the registry.
            </p>
          </header>

          {error && (
            <div className="mb-6">
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            {loading ? (
              <motion.div
                key="loading"
                exit={{ opacity: 0 }}
                className="flex flex-col gap-4"
                aria-live="polite"
              >
                <span className="sr-only">Checking your organisation status</span>
                <Skeleton className="h-[132px]" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Skeleton className="h-[196px]" />
                  <Skeleton className="h-[196px]" />
                </div>
              </motion.div>
            ) : invitations.length > 0 ? (
              <Panel key="invitations">
                <SectionLabel icon={Mail}>
                  {invitations.length === 1
                    ? "You have an invitation"
                    : `You have ${invitations.length} invitations`}
                </SectionLabel>
                <div className="flex flex-col gap-4">
                  {invitations.map((invitation) => (
                    <InvitationCard
                      key={invitation.id}
                      invitation={invitation}
                      onDecided={afterDecision}
                    />
                  ))}
                </div>
              </Panel>
            ) : waiting ? (
              <Panel key="waiting">
                <WaitingState
                  joinRequest={joinRequest}
                  orgRequest={orgRequest}
                  onWithdrawn={load}
                />
              </Panel>
            ) : choice === "join" ? (
              <Panel key="join">
                <OrgPicker onDone={load} onCancel={() => setChoice(null)} />
              </Panel>
            ) : choice === "create" ? (
              <Panel key="create">
                <CreateOrgForm onDone={load} onCancel={() => setChoice(null)} />
              </Panel>
            ) : (
              <Panel key="choose">
                <div className="grid gap-4 sm:grid-cols-2">
                  <ChoiceCard
                    icon={Search}
                    title="Join an organisation"
                    desc="Find your institution and ask an administrator to add you."
                    cta="Find my organisation"
                    onClick={() => setChoice("join")}
                  />
                  <ChoiceCard
                    icon={PlusCircle}
                    title="Create an organisation"
                    desc="Not listed yet? Propose it for a NEED administrator to review."
                    cta="Propose a new one"
                    onClick={() => setChoice("create")}
                  />
                </div>
              </Panel>
            )}
          </AnimatePresence>
        </section>
      </main>
    </div>
  );
}

function Panel({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return (
    <h2 className="mb-4 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
      <Icon size={12} />
      {children}
    </h2>
  );
}

function ChoiceCard({ icon: Icon, title, desc, cta, onClick }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.985 }}
      transition={{ type: "spring", stiffness: 380, damping: 24 }}
      className="group flex min-h-[196px] flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-colors hover:border-green-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2"
    >
      <div>
        <h3 className="text-lg font-black tracking-tight text-slate-900 sm:text-xl">{title}</h3>
        <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg">{desc}</p>
      </div>
      <span className="mt-6 inline-flex items-center gap-2 self-start rounded-xl bg-green-600 px-5 py-3 text-base font-bold text-white transition-colors group-hover:bg-green-700">
        <Icon size={18} strokeWidth={2.5} />
        {cta}
      </span>
    </motion.button>
  );
}

function WaitingState({ joinRequest, orgRequest, onWithdrawn }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const withdraw = async () => {
    setBusy(true);
    setError("");
    try {
      await API.post(
        joinRequest ? "/api/orgs/join-requests/withdraw" : "/api/orgs/org-requests/withdraw"
      );
      await onWithdrawn();
    } catch (err) {
      setError(err.response?.data?.detail || "We could not withdraw that just now.");
      setBusy(false);
    }
  };

  const managers = joinRequest?.managers || [];

  return (
    <div className="overflow-hidden rounded-2xl border border-amber-300/70 bg-white shadow-sm">
      <div className="flex items-start gap-4 bg-amber-50/70 px-6 py-5">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <motion.span
            animate={{ rotate: [0, 0, 360] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Clock size={17} strokeWidth={2.5} />
          </motion.span>
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-amber-700">
            Awaiting approval
          </p>
          <p className="mt-1.5 text-base font-bold tracking-tight text-slate-900">
            {joinRequest ? joinRequest.org_name : orgRequest.proposed_name}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 py-5">
        <p className="text-sm leading-relaxed text-slate-600">
          {joinRequest ? (
            <>
              Your request to join{" "}
              <span className="font-semibold text-slate-900">{joinRequest.org_name}</span> is
              recorded against that organisation. You will be let in the moment an
              administrator adds you — no need to sign in again.
            </>
          ) : (
            <>
              <span className="font-semibold text-slate-900">{orgRequest.proposed_name}</span>{" "}
              has been created and is waiting for a NEED administrator to enable it. You
              become its first administrator as soon as they do.
            </>
          )}
        </p>

        {managers.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              Waiting on
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {managers.map((manager) => (
                <li key={manager.email} className="text-sm text-slate-700">
                  <span className="font-semibold">{manager.name || manager.email}</span>
                  {manager.name && (
                    <span className="text-slate-500"> · {manager.email}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ErrorNote>{error}</ErrorNote>

        <div className="flex justify-end">
          <ActionButton
            variant="quiet"
            busy={busy}
            busyLabel="Withdrawing…"
            onClick={withdraw}
          >
            Withdraw {joinRequest ? "request" : "proposal"}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
